import { useEffect, useMemo, useState } from 'react';
import { analyzeGinHand, ginCardValue, sortHand, type GinHandResult, type GinLegal, type GinView } from '@cardhub/engine';
import { PlayingCard, shortCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

export function GinTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as GinView;
  const legal = useGame((s) => s.legal) as GinLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const narrow = useNarrow();
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const pileSize = narrow ? 'md' : 'lg';
  const opp = mySeat !== null ? 1 - mySeat : 1;
  const handOver = view.phase === 'HAND_END' || view.phase === 'MATCH_END';
  const lastResult = view.handResults.at(-1);
  const discarding = Boolean(myTurn && legal && legal.discardable.length > 0);

  // Melds first (runs and sets), deadwood after, so the player can see what is still loose.
  const analysis = useMemo(() => (view.cards.length > 0 ? analyzeGinHand(view.cards) : null), [view.cards]);
  const layout = useMemo(() => {
    if (!analysis) return { groups: [] as string[][], rest: [] as string[], points: 0 };
    const arr = analysis.arrangement();
    let points = arr.points;
    // With 11 cards in hand the score that matters is the best one after the discard.
    if (view.cards.length > 10) points = Math.min(...view.cards.map((_, i) => analysis.deadwood(i)));
    return { groups: arr.melds.map((m) => m.cards), rest: sortHand(arr.deadwood), points };
  }, [analysis, view.cards]);

  // A fresh deal or turn starts with nothing selected.
  useEffect(() => {
    setSelected(null);
  }, [view.hand, view.phase, view.turn]);

  const knockSelected = selected && legal?.knockable.includes(selected) ? selected : null;
  const knockPoints = knockSelected && analysis ? analysis.deadwood(view.cards.indexOf(knockSelected)) : null;

  const pick = (c: string) => {
    if (discarding && legal?.discardable.includes(c)) setSelected(selected === c ? null : c);
  };

  const cards = [
    ...layout.groups.flatMap((g, gi) =>
      g.map((c, k) => (
        <span key={c} style={{ display: 'inline-block', marginRight: k === g.length - 1 ? 10 : 0 }} data-group={gi}>
          <PlayingCard id={c} size={narrow ? 'md' : 'lg'} selected={selected === c} unplayable={discarding && c === view.locked} onClick={() => pick(c)} />
        </span>
      )),
    ),
    ...layout.rest.map((c) => (
      <span key={c} style={{ display: 'inline-block' }}>
        <PlayingCard id={c} size={narrow ? 'md' : 'lg'} selected={selected === c} unplayable={discarding && c === view.locked} onClick={() => pick(c)} />
      </span>
    )),
  ];

  const outcomeLine = (r: GinHandResult) => {
    if (r.outcome === 'DRAW' || r.winner === null) return t('gr.drawHand');
    if (r.outcome === 'GIN') return t('gr.ginned', { name: name(r.winner) });
    if (r.outcome === 'UNDERCUT') return t('gr.undercut', { name: name(r.winner) });
    return t('gr.knocked', { name: name(r.winner) });
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={2}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.scores[s] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {s !== mySeat && !handOver && <span className="tag">{view.handCounts[s]}</span>}
                {(view.handsWon[s] ?? 0) > 0 && <span className="tag seen">{t('gr.wins', { n: view.handsWon[s]! })}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('gr.hand', { n: view.hand })} · {t('gr.goal', { n: view.config.targetScore })}
            {view.phase === 'FIRST' && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
          </div>
          {handOver && lastResult ? (
            <div className="card glass" style={{ textAlign: 'center', maxHeight: '52vh', overflow: 'auto' }} role="status">
              <strong>{outcomeLine(lastResult)}</strong>
              {lastResult.winner !== null && <span className="pill accent" style={{ marginLeft: 8 }}>+{lastResult.points}</span>}
              {[opp, mySeat ?? 0].map((s) => (
                <div key={s} style={{ marginTop: 8 }}>
                  <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{name(s)}</div>
                  <div className="melds" style={{ justifyContent: 'center' }}>
                    {(lastResult.melds[s] ?? []).map((m, i) => (
                      <div key={i} className="meld">
                        {m.cards.map((c) => (
                          <PlayingCard key={c} id={c} size="sm" />
                        ))}
                      </div>
                    ))}
                    {(lastResult.deadwood[s] ?? []).length > 0 && (
                      <div className="meld" style={{ opacity: 0.75 }} aria-label={t('gr.deadwoodLabel')}>
                        {lastResult.deadwood[s]!.map((c) => (
                          <PlayingCard key={c} id={c} size="sm" />
                        ))}
                      </div>
                    )}
                  </div>
                  {(lastResult.deadwood[s] ?? []).length > 0 && (
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {t('gr.deadwood', { n: lastResult.deadwood[s]!.reduce((n, c) => n + ginCardValue(c), 0) })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="row" style={{ gap: 18, alignItems: 'flex-end', justifyContent: 'center' }}>
                <button className="col" style={{ alignItems: 'center', gap: 4, background: 'none', border: 0 }} disabled={!myTurn || !legal?.canDrawStock || acting} onClick={() => void act({ type: 'draw', from: 'stock' })} aria-label={t('mr.drawStock')}>
                  <PlayingCard faceDown size={pileSize} className={myTurn && legal?.canDrawStock ? 'playable' : ''} />
                  <span className="center-note">{t('mr.stock', { n: view.stockCount })}</span>
                </button>
                <button
                  className="col"
                  style={{ alignItems: 'center', gap: 4, background: 'none', border: 0 }}
                  disabled={!myTurn || !(legal?.canDrawDiscard || legal?.canTakeUpcard) || acting}
                  onClick={() => void act(legal?.canTakeUpcard ? { type: 'takeUpcard' } : { type: 'draw', from: 'discard' })}
                  aria-label={view.phase === 'FIRST' ? t('gr.upcard') : t('mr.pickDiscard')}
                >
                  {view.discardTop ? <PlayingCard id={view.discardTop} size={pileSize} className={myTurn && (legal?.canDrawDiscard || legal?.canTakeUpcard) ? 'playable' : ''} /> : <span className={`pcard ${pileSize}`} />}
                  <span className="center-note">{view.phase === 'FIRST' ? t('gr.upcard') : t('mr.pickDiscard')}</span>
                </button>
              </div>
              {view.phase === 'DRAW' && myTurn && legal && !legal.canDrawDiscard && <div className="center-note">{t('gr.stockOnly')}</div>}
            </>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.scores[mySeat] ?? 0} pts{!handOver && view.cards.length > 0 ? ` · ${t('gr.deadwood', { n: layout.points })}` : ''}</> : null} />
      </div>
      <div className="table-bottom">
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.6}>
          {cards}
        </Hand>
        <div className="action-bar">
          {myTurn && legal?.canPassUpcard && (
            <>
              <button className="btn ghost" disabled={acting} onClick={() => void act({ type: 'passUpcard' })}>
                {t('gr.pass')}
              </button>
              <button className="btn primary" disabled={acting} onClick={() => void act({ type: 'takeUpcard' })}>
                {t('gr.take')}
              </button>
            </>
          )}
          {discarding && (
            <>
              <button className="btn primary" disabled={!selected || acting} onClick={() => selected && void act({ type: 'discard', card: selected })}>
                {t('mr.discard')}
                {selected ? ` ${shortCard(selected)}` : ''}
              </button>
              {legal && legal.knockable.length > 0 && (
                <button
                  className="btn gold"
                  disabled={!knockSelected || acting}
                  onClick={() => knockSelected && void act({ type: 'knock', card: knockSelected })}
                  aria-label={knockPoints === 0 ? t('gr.gin') : t('gr.knock')}
                >
                  {knockPoints === 0 ? t('gr.gin') : t('gr.knock')}
                  {knockSelected ? ` · ${knockPoints === 0 ? '0' : knockPoints}` : ''}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 110 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.handResults.map((r) => (
              <tr key={r.hand}>
                <td>H{r.hand}</td>
                {[0, 1].map((s) => (
                  <td key={s} className="num" style={{ color: r.winner === s ? 'var(--accent)' : undefined }}>
                    {r.winner === s ? `+${r.points}` : '–'}
                    {r.winner === s && <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{r.outcome === 'GIN' ? t('gr.gin') : r.outcome === 'UNDERCUT' ? t('gr.undercutShort') : t('gr.knock')}</div>}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.totals.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('gr.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}
